/**
 * Cross-language skill synonyms for the CV-to-job match (TEC-711). A Finnish CV says
 * "Asiakaspalvelu" where an English job lists "Customer service"; without this the literal match
 * calls that skill missing. The groups are mined from the job data (skill-synonyms.generated.ts)
 * plus hand-curated Swedish forms (skill-synonyms-sv.ts).
 *
 * The data is about 70KB, and the matcher runs on every search result card, so it is loaded on
 * demand (loadSkillSynonyms) only for visitors who have a CV. Until it has loaded, skillVariants
 * returns just the name itself and matching stays literal.
 */
import { SV_SKILL_SYNONYMS } from './skill-synonyms-sv';

let index: Map<string, readonly string[]> | null = null;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();

/** Builds the lookup from groups of already-normalised names. Exported for tests. */
export function registerSkillSynonymGroups(groups: readonly (readonly string[])[], normalise: (s: string) => string): void {
  const next = new Map<string, readonly string[]>();
  const merged = groups.map((g) => [...g]);
  for (const [anchor, ...sv] of SV_SKILL_SYNONYMS) {
    const group = merged.find((g) => g.includes(anchor));
    if (group) group.push(...sv.map(normalise));
    else merged.push([anchor, ...sv.map(normalise)]);
  }
  for (const group of merged) {
    const unique = Array.from(new Set(group));
    for (const name of unique) {
      // A name in two groups keeps the first (the mined groups are sorted most common first).
      if (!next.has(name)) next.set(name, unique);
    }
  }
  index = next;
  listeners.forEach((fn) => fn());
}

export function skillSynonymsLoaded(): boolean {
  return index !== null;
}

/** Loads the synonym data once; later calls return the same promise. */
export function loadSkillSynonyms(normalise: (s: string) => string): Promise<void> {
  loading ??= import('./skill-synonyms.generated')
    .then((m) => registerSkillSynonymGroups(m.MINED_SKILL_SYNONYM_GROUPS, normalise))
    .catch(() => {
      // Matching stays literal; allow a later retry.
      loading = null;
    });
  return loading;
}

export function onSkillSynonymsLoaded(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Every name meaning the same as this normalised name, itself first. */
export function skillVariants(normalised: string): readonly string[] {
  const group = index?.get(normalised);
  if (!group) return [normalised];
  return [normalised, ...group.filter((g) => g !== normalised)];
}
